const API = process.env.REACT_APP_API_URL || '/api';

function getToken() {
  return localStorage.getItem('isi_token');
}

export function setToken(token) {
  localStorage.setItem('isi_token', token);
}

export function clearToken() {
  localStorage.removeItem('isi_token');
  localStorage.removeItem('isi_user');
}

export function getStoredUser() {
  const u = localStorage.getItem('isi_user');
  return u ? JSON.parse(u) : null;
}

export function setStoredUser(user) {
  localStorage.setItem('isi_user', JSON.stringify(user));
}

export async function fetchApi(endpoint, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API}${endpoint}`, { ...options, headers });
  if (res.status === 401) {
    // Only treat 401 as "session expired" when a token was actually attached
    // AND the call was not the login endpoint itself. Otherwise the user
    // sees "Session expirée" for what was actually a bad-credentials error
    // (the backend returns 401 "Email ou mot de passe incorrect" on a
    // failed login, but at that point there is no session to expire).
    const isLoginEndpoint = endpoint === '/auth/login';
    if (token && !isLoginEndpoint) {
      clearToken();
      window.location.href = '/login';
      throw new Error('Session expirée');
    }
    // No token (or login attempt) -> surface the real server error
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.error || err.detail || 'Email ou mot de passe incorrect');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.error || err.detail || `Erreur API: ${res.status}`);
  }
  return res.json();
}

// Auth
export const login = (data) => fetchApi('/auth/login', { method: 'POST', body: JSON.stringify(data) });

// Parcours de connexion renforce (jury) : chaque etape est identifiee par un
// jeton de challenge, distinct du jeton de session et sans acces aux routes metier.
export const onboardingPassword = (challenge_token, new_password) =>
  fetchApi('/auth/onboarding/password', { method: 'POST', body: JSON.stringify({ challenge_token, new_password }) });
export const mfaSetup = (challenge_token) =>
  fetchApi('/auth/mfa/setup', { method: 'POST', body: JSON.stringify({ challenge_token }) });
export const mfaActivate = (challenge_token, code) =>
  fetchApi('/auth/mfa/activate', { method: 'POST', body: JSON.stringify({ challenge_token, code }) });
export const mfaVerify = (challenge_token, code) =>
  fetchApi('/auth/mfa/verify', { method: 'POST', body: JSON.stringify({ challenge_token, code }) });
export const mfaHelp = (challenge_token, email, message) =>
  fetchApi('/auth/mfa/help', { method: 'POST', body: JSON.stringify({ challenge_token, email, message }) });
// Engagement de confidentialité du jury. Le texte vient du serveur : la preuve
// enregistrée porte son empreinte, il doit donc être celui qui est affiché.
export const getJuryLegalDocument = () => fetchApi('/auth/legal/jury');
export const acceptJuryLegal = (challenge_token, accepted) =>
  fetchApi('/auth/legal/accept', { method: 'POST', body: JSON.stringify({ challenge_token, accepted }) });
export const getLegalAcceptances = () => fetchApi('/admin/legal-acceptances');
export const getLegalAcceptancesExportUrl = () => `${API}/admin/legal-acceptances/export.csv`;

export const register = (data) => fetchApi('/auth/register', { method: 'POST', body: JSON.stringify(data) });
export const getMe = () => fetchApi('/auth/me');
// Password reset flow — both endpoints are PUBLIC (no JWT required)
export const requestPasswordReset = (email) =>
  fetchApi('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
export const resetPassword = (token, newPassword) =>
  fetchApi('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, new_password: newPassword }) });
export const changePassword = (currentPassword, newPassword) =>
  fetchApi('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });

// Startups (public)
export const getTopStartups = () => fetchApi('/startups/top');
export const getStartups = (params) => fetchApi(`/startups?${new URLSearchParams(params)}`);
export const getStartup = (id) => fetchApi(`/startups/${id}`);
export const getStartupRadar = (id) => fetchApi(`/startups/${id}/radar`);
export const searchStartups = (params) => fetchApi(`/search?${new URLSearchParams(params)}`);

// Startup events
export const getMyStartupEvents = () => fetchApi('/startup-events/mine');
export const getStartupEvents = (startupId) => fetchApi('/startup-events/' + startupId);

// Startup portal (auth)
export const getMyStartup = () => fetchApi('/startups/mine');
export const updateMyStartup = (data) => fetchApi('/startups/mine', { method: 'PUT', body: JSON.stringify(data) });
export const getStartupDocuments = (id) => fetchApi(`/startups/${id}/documents`);
export const uploadDocument = (id, data) => fetchApi(`/startups/${id}/documents`, { method: 'POST', body: JSON.stringify(data) });
export const deleteDocument = (sid, did) => fetchApi(`/startups/${sid}/documents/${did}`, { method: 'DELETE' });
export const toggleTrophyParticipation = (participate) => fetchApi('/startups/mine/trophy-participate', { method: 'POST', body: JSON.stringify({ participate }) });
export const getStartupContacts = (id) => fetchApi(`/startups/${id}/contact-requests`);

// QIT
export const getQitDefinition = () => fetchApi('/qit/definition');
export const getQitResponses = (id, year) => fetchApi(`/qit/${id}${year ? `?year=${year}` : ''}`);
export const getQitYears = (id) => fetchApi(`/qit/${id}/years`);
export const prefillQit = (id, source_year, target_year) => fetchApi(`/qit/${id}/prefill`, { method: 'POST', body: JSON.stringify({ source_year, target_year }) });
export const saveQitResponses = (id, responses, year) => fetchApi(`/qit/${id}`, { method: 'PUT', body: JSON.stringify({ responses, year }) });
export const submitQit = (id, year, certificationAccepted) => fetchApi(`/qit/${id}/submit`, {
  method: 'POST',
  body: JSON.stringify({ year, certification_accepted: certificationAccepted }),
});
export const getQitSubmissionStatus = (id, year) => fetchApi(`/qit/${id}/submission-status?year=${year}`);

// Investor
export const getWatchlist = () => fetchApi('/investors/me/watchlist');
export const addToWatchlist = (startupId) => fetchApi('/investors/me/watchlist', { method: 'POST', body: JSON.stringify({ startup_id: startupId }) });
export const removeFromWatchlist = (startupId) => fetchApi(`/investors/me/watchlist/${startupId}`, { method: 'DELETE' });
export const checkWatchlist = (startupId) => fetchApi(`/investors/me/watchlist/check/${startupId}`);
export const sendContact = (data) => fetchApi('/investors/me/contact', { method: 'POST', body: JSON.stringify(data) });
export const getMyContacts = () => fetchApi('/investors/me/contacts');
export const getInvestorProfile = () => fetchApi('/investors/me/profile');
export const updateInvestorProfile = (data) => fetchApi('/investors/me/profile', { method: 'PUT', body: JSON.stringify(data) });
export const getMyAlerts = () => fetchApi('/investors/me/alerts');
export const createAlert = (criteria) => fetchApi('/investors/me/alerts', { method: 'POST', body: JSON.stringify({ criteria }) });
export const deleteAlert = (id) => fetchApi(`/investors/me/alerts/${id}`, { method: 'DELETE' });
export const compareStartups = (ids) => fetchApi('/investors/compare', { method: 'POST', body: JSON.stringify({ ids }) });

// Portfolio
export const getPortfolio = () => fetchApi('/investors/me/portfolio');
export const addToPortfolio = (data) => fetchApi('/investors/me/portfolio', { method: 'POST', body: JSON.stringify(data) });
export const updatePortfolio = (startupId, data) => fetchApi(`/investors/me/portfolio/${startupId}`, { method: 'PUT', body: JSON.stringify(data) });
export const removeFromPortfolio = (startupId) => fetchApi(`/investors/me/portfolio/${startupId}`, { method: 'DELETE' });

// NDA
export const recordNdaDownload = () => fetchApi('/nda/download', { method: 'POST' });
export const getNdaTemplateUrl = () => `${API}/nda/template`;
export const getNdaStatus = () => fetchApi('/nda/status');
export async function uploadNdaFile(file) {
  const formData = new FormData();
  formData.append('nda_file', file);
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API}/nda/upload`, { method: 'POST', headers, body: formData });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || err.error || `Erreur: ${res.status}`); }
  return res.json();
}
export const getAdminNda = (startupId) => fetchApi(`/nda/admin/${startupId}`);
export const validateNda = (startupId) => fetchApi(`/nda/admin/${startupId}/validate`, { method: 'PUT' });
export const rejectNda = (startupId, reason) => fetchApi(`/nda/admin/${startupId}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) });
export const getAdminPendingNdas = () => fetchApi('/nda/admin-pending');
export const downloadNdaFile = (startupId) => `${API}/nda/admin/${startupId}/download-file`;
export const getNdaTemplateInfo = () => fetchApi('/nda/template-info');
export async function uploadNdaTemplate(file) {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API}/nda/admin-upload-template`, { method: 'POST', headers, body: formData });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || err.message || `Erreur: ${res.status}`); }
  return res.json();
}

// Business Plan status (used by QIT page and Data Room banner)
export const getBpStatus = () => fetchApi('/startups/mine/bp-status');

// Data Room (production)
export const getDataroomDocuments = () => fetchApi('/dataroom/documents');
export async function uploadDataroomDocument(file, category, documentType) {
  const formData = new FormData();
  formData.append('document', file);
  formData.append('category', category);
  formData.append('document_type', documentType);
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API}/dataroom/upload`, { method: 'POST', headers, body: formData });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || err.error || `Erreur: ${res.status}`); }
  return res.json();
}
export const deleteDataroomDocument = (docId) => fetchApi(`/dataroom/documents/${docId}`, { method: 'DELETE' });
export const getDataroomDownloadUrl = (docId) => `${API}/dataroom/download/${docId}`;
export const getAdminDataroom = (startupId) => fetchApi(`/dataroom/admin/${startupId}`);
export const getAdminDataroomDownloadUrl = (startupId, docId) => `${API}/dataroom/admin/${startupId}/download/${docId}`;
export const updateDataroomDocStatus = (docId, status, reason, adminComment) => fetchApi(`/dataroom/admin/documents/${docId}/status`, { method: 'PUT', body: JSON.stringify({ status, reason, admin_comment: adminComment }) });
export const updateDocStartupComment = (docId, comment) => fetchApi(`/dataroom/documents/${docId}/comment`, { method: 'PUT', body: JSON.stringify({ comment }) });

// Notifications
export const getAdminNotifications = () => fetchApi('/notifications/admin');

// Stats
export const getStats = () => fetchApi('/stats');
export const getMatrix = () => fetchApi('/stats/matrix');
export const getTrophyStats = () => fetchApi('/stats/trophy');

// Admin
export const getDossiers = (status) => fetchApi(`/admin/dossiers${status ? `?status=${status}` : ''}`);
export const getDossierDetail = (id) => fetchApi(`/admin/dossiers/${id}`);
export const validateDossier = (id, status) => fetchApi(`/admin/dossiers/${id}/validate`, { method: 'PUT', body: JSON.stringify({ status }) });
export const updateDossierAction = (id, action) => fetchApi(`/admin/dossiers/${id}/action`, { method: 'PUT', body: JSON.stringify({ dossier_action: action }) });
export const deleteStartup = (id) => fetchApi(`/admin/dossiers/${id}`, { method: 'DELETE' });
export const getStartupInvestors = (id) => fetchApi(`/admin/dossiers/${id}/investors`);
export const updateDocStatus = (id, status) => fetchApi(`/admin/documents/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
export const getAuditAlerts = () => fetchApi('/admin/audit-alerts');
export const resolveAlert = (id) => fetchApi(`/admin/audit-alerts/${id}/resolve`, { method: 'PUT' });
export const triggerScoring = (id) => fetchApi(`/admin/scoring/${id}/trigger`, { method: 'POST' });
// Presence (Outils > Utilisateurs connectés). fetch brut et erreurs ignorées :
// un jeton expiré resté en localStorage ne doit pas renvoyer vers /login un
// visiteur de la page d'accueil, comme le ferait fetchApi sur un 401.
export function sendHeartbeat() {
  const token = getToken();
  if (!token) return Promise.resolve();
  return fetch(`${API}/auth/heartbeat`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
    .catch(() => {});
}
export function endSession() {
  const token = getToken();
  if (!token) return;
  fetch(`${API}/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, keepalive: true })
    .catch(() => {});
}
export const getConnectedUsers = () => fetchApi('/admin/tools/connected-users');

export const getScoringStatus = () => fetchApi('/admin/scoring/status');
export const recomputeAllScores = (onlyStale) =>
  fetchApi('/admin/scoring/recompute-all', {
    method: 'POST',
    body: JSON.stringify({ only_stale: !!onlyStale }),
  });
export const publishScore = (id, published) => fetchApi(`/admin/dossiers/${id}/publish-score`, { method: 'PUT', body: JSON.stringify({ published }) });
export const unlockCandidature = (id) => fetchApi(`/admin/dossiers/${id}/unlock-candidature`, { method: 'POST' });
export async function exportCandidatesCsv(ids) {
  const token = getToken();
  const res = await fetch(`${API}/admin/dossiers/export-candidates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(`Export échoué (HTTP ${res.status})`);
  return res.blob();
}
export const getUsers = () => fetchApi('/admin/users');
export const createUser = (data) => fetchApi('/admin/users', { method: 'POST', body: JSON.stringify(data) });
export const resetUserMfa = (id) => fetchApi(`/admin/users/${id}/reset-mfa`, { method: 'POST' });
export const resetUserPasswordGenerated = (id) =>
  fetchApi(`/admin/users/${id}/reset-password-generated`, { method: 'POST' });
export const getSupportRequests = (status = 'open') => fetchApi(`/admin/support-requests?status=${status}`);
export const resolveSupportRequest = (id) =>
  fetchApi(`/admin/support-requests/${id}/resolve`, { method: 'POST' });
export const updateUser = (id, data) => fetchApi(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteUser = (id) => fetchApi(`/admin/users/${id}`, { method: 'DELETE' });
export const getSectorWeights = () => fetchApi('/admin/sector-weights');
export const updateSectorWeights = (id, data) => fetchApi(`/admin/sector-weights/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const getAnalytics = () => fetchApi('/admin/analytics');
export const getCandidatesCompleteness = () => fetchApi('/admin/analytics/candidates-completeness');
export const getAnalyticsBySector = (filters = {}) => {
  const qs = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v !== '' && v != null)
  ).toString();
  return fetchApi(`/admin/analytics/by-sector${qs ? `?${qs}` : ''}`);
};
export async function exportBySectorCsv(filters = {}) {
  const qs = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v !== '' && v != null)
  ).toString();
  const token = getToken();
  const res = await fetch(`${API}/admin/analytics/by-sector/export${qs ? `?${qs}` : ''}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Export échoué (HTTP ${res.status})`);
  return res.blob();
}
export const getExportStartups = () => fetchApi('/admin/exports/startups');
export const toggleTrophy = (id, campaign_id, participate) => fetchApi(`/admin/startups/${id}/trophy`, { method: 'POST', body: JSON.stringify({ campaign_id, participate }) });

// Scraping
export const getScrapingRuns = () => fetchApi('/admin/scraping/runs');
export const getScrapingRecords = (params) => fetchApi(`/admin/scraping/records${params ? '?' + new URLSearchParams(params) : ''}`);
export const triggerScraping = (source) => fetchApi('/admin/scraping/trigger', { method: 'POST', body: JSON.stringify({ source }) });

// Campaigns
export const getCampaigns = () => fetchApi('/admin/campaigns');
export const createCampaign = (data) => fetchApi('/admin/campaigns', { method: 'POST', body: JSON.stringify(data) });
export const getJuryMembers = (campaignId) => fetchApi(`/admin/jury-members?campaign_id=${campaignId}`);
export const addJuryMember = (data) => fetchApi('/admin/jury-members', { method: 'POST', body: JSON.stringify(data) });
export const updateJuryMember = (id, data) => fetchApi(`/admin/jury-members/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const removeJuryMember = (id) => fetchApi(`/admin/jury-members/${id}`, { method: 'DELETE' });

// Selection des startups soumises au jury
export const getJurySelection = (campaignId) => fetchApi(`/admin/campaigns/${campaignId}/jury-selection`);
export const setJurySelection = (campaignId, startupIds, visible) =>
  fetchApi(`/admin/campaigns/${campaignId}/jury-selection`, {
    method: 'PUT',
    body: JSON.stringify({ startup_ids: startupIds, visible }),
  });

// Jury
export const getJuryCampaigns = () => fetchApi('/jury/campaigns');
export const getCampaignParticipants = (id) => fetchApi(`/jury/campaigns/${id}/participants`);
// Messagerie privee entre membres du jury.
export const getJuryMsgMembers = (campaignId) => fetchApi(`/jury/messages/campaigns/${campaignId}/members`);
export const getJuryMsgMyComments = (campaignId) => fetchApi(`/jury/messages/campaigns/${campaignId}/my-comments`);
export const getJuryThreads = (campaignId) => fetchApi(`/jury/messages/campaigns/${campaignId}/threads`);
export const createJuryThread = (campaignId, data) =>
  fetchApi(`/jury/messages/campaigns/${campaignId}/threads`, { method: 'POST', body: JSON.stringify(data) });
export const getJuryThread = (threadId) => fetchApi(`/jury/messages/threads/${threadId}`);
export const replyJuryThread = (threadId, data) =>
  fetchApi(`/jury/messages/threads/${threadId}/messages`, { method: 'POST', body: JSON.stringify(data) });
export const getJuryUnreadCount = () => fetchApi('/jury/messages/unread-count');

// Data Room en consultation : pieces ouvertes au jury.
export const getParticipantDocuments = (startupId) => fetchApi(`/jury/participants/${startupId}/documents`);
// Visionneuse : le fichier source n'est jamais servi, seules des images de page le sont.
export const getParticipantDocumentPreview = (startupId, docId) =>
  fetchApi(`/jury/participants/${startupId}/documents/${docId}/preview`);
export const getParticipantDocumentPageUrl = (startupId, docId, page) =>
  `${API}/jury/participants/${startupId}/documents/${docId}/page/${page}`;
export const getCampaignRanking = (id) => fetchApi(`/jury/campaigns/${id}/ranking`);
export const getParticipantFiche = (id) => fetchApi(`/jury/participants/${id}/fiche`);
export const getJuryComments = (campaignId) => fetchApi(`/jury/comments/${campaignId}`);
export const saveJuryStartupComment = (campaignId, startupId, comment) => fetchApi(`/jury/comments/${campaignId}/startup/${startupId}`, { method: 'PUT', body: JSON.stringify({ comment }) });
export const saveJuryGeneralComment = (campaignId, comment) => fetchApi(`/jury/comments/${campaignId}/general`, { method: 'PUT', body: JSON.stringify({ comment }) });

// Portefeuille d'investissement virtuel du jury (15 M€ par juré).
// Dépouillement vu par un juré : accessible APRÈS clôture seulement (409 sinon).
// Ne contient pas le nominatif — ni qui a voté, ni qui a mis quoi.
export const getJuryVoteResults = (campaignId) => fetchApi(`/jury/campaigns/${campaignId}/results`);
export const getJuryPortfolio = (campaignId) => fetchApi(`/jury/portfolio/${campaignId}`);
export const saveJuryPortfolio = (campaignId, allocations) =>
  fetchApi(`/jury/portfolio/${campaignId}`, { method: 'PUT', body: JSON.stringify({ allocations }) });

// Dépouillement, réservé à l'administrateur.
export const getJuryResults = (campaignId) => fetchApi(`/admin/campaigns/${campaignId}/jury-results`);
export const setJuryVoteLock = (campaignId, locked) =>
  fetchApi(`/admin/campaigns/${campaignId}/jury-vote/lock`, { method: 'POST', body: JSON.stringify({ locked }) });
export const getJuryResultsExportUrl = (campaignId) =>
  `${API}/admin/campaigns/${campaignId}/jury-results/export.csv`;

// Coup de cœur : une seule startup par juré et par campagne. setJuryFavorite
// remplace le précédent et renvoie `replaced` pour en rendre compte.
// DÉSACTIVÉ — le jury a renoncé à cette distinction. Ces appels ne sont plus
// faits (COUP_DE_COEUR dans JurySpace.js) et le serveur les refuse en écriture
// (COUP_DE_COEUR_ACTIF dans routes/jury.py). Conservés pour un rétablissement.
export const getJuryFavorite = (campaignId) => fetchApi(`/jury/favorite/${campaignId}`);
export const setJuryFavorite = (campaignId, startupId) =>
  fetchApi(`/jury/favorite/${campaignId}/startup/${startupId}`, { method: 'PUT' });
export const clearJuryFavorite = (campaignId) => fetchApi(`/jury/favorite/${campaignId}`, { method: 'DELETE' });

// BODACC
export const getBodaccStatus = () => fetchApi('/bodacc/status');
export const getBodaccForStartup = (id) => fetchApi(`/bodacc/startup/${id}`);
export const getBodaccRuns = (type) => fetchApi(`/bodacc/runs${type ? `?type=${type}` : ''}`);
export const triggerBodacc = () => fetchApi('/bodacc/trigger', { method: 'POST' });
export const getBodaccProgress = (runId) => fetchApi(`/bodacc/progress/${runId}`);
export const reSearchAllBodacc = () => fetchApi('/bodacc/re-search-all', { method: 'POST' });
export const autoSearchSiret = () => fetchApi('/siret/auto-search', { method: 'POST' });
export const autoInitBodacc = () => fetchApi('/bodacc/auto-init', { method: 'POST' });
export const fetchBodaccForOne = (startupId) => fetchApi(`/bodacc/fetch-one/${startupId}`, { method: 'POST' });

// BODACC — Cessions d'entreprises
export const triggerBodaccCessions = () => fetchApi('/bodacc/cessions/trigger', { method: 'POST' });
export const getBodaccCessions = (params) => fetchApi(`/bodacc/cessions${params ? '?' + new URLSearchParams(params) : ''}`);
export const getBodaccCessionsForStartup = (id) => fetchApi(`/bodacc/cessions/startup/${id}`);
export function getBodaccCessionsExportUrl(params) {
  const qs = params ? '?' + new URLSearchParams(params) : '';
  return `${API}/bodacc/cessions/export.csv${qs}`;
}

// BODACC — Procédures globales (cessions + liquidations + redressements)
// Recherche directe sur l'API BODACC, indépendante des startups en base, sur 2 ans glissants.
export const triggerBodaccProcedures = () => fetchApi('/bodacc/procedures/trigger', { method: 'POST' });
export const extendBodaccProcedures = () => fetchApi('/bodacc/procedures/extend', { method: 'POST' });
export const getBodaccProceduresKpis = () => fetchApi('/bodacc/procedures/kpis');
export const getBodaccProcedures = (params) => fetchApi(`/bodacc/procedures${params ? '?' + new URLSearchParams(params) : ''}`);
export function getBodaccProceduresExportUrl(params) {
  const qs = params ? '?' + new URLSearchParams(params) : '';
  return `${API}/bodacc/procedures/export.csv${qs}`;
}

// News / Investor Digest
export const getInvestorDigest = () => fetchApi('/news/investor-digest');

// KYC
export const checkEmail = (email) => fetchApi('/kyc/check-email', { method: 'POST', body: JSON.stringify({ email }) });
export const verifyCode = (email, code) => fetchApi('/kyc/verify-code', { method: 'POST', body: JSON.stringify({ email, code }) });
export const uploadKycDocument = (data) => fetchApi('/kyc/upload-document', { method: 'POST', body: JSON.stringify(data) });
export const submitKyc = (data) => fetchApi('/kyc/submit', { method: 'POST', body: JSON.stringify(data) });
export const getKycStatus = () => fetchApi('/kyc/status');

// WebSummary / URL check
export const checkSiteUrl = (url) => fetchApi('/websummary/check-url', { method: 'POST', body: JSON.stringify({ url }) });
export const checkSiteUrlPublic = (url) => fetchApi('/websummary/check-url-public', { method: 'POST', body: JSON.stringify({ url }) });
